/**
 * Login Page
 *
 * Entry point for unauthenticated users.
 * Displays sign-in button that redirects to Microsoft Entra External ID.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container, Paper, Grid } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useUser } from '../contexts/UserContext';

export const Login: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const { status } = useUser();
  const navigate = useNavigate();

  // Redirect based on intent + onboarding status when both are available
  useEffect(() => {
    if (!isAuthenticated || !status) return;

    const intent = sessionStorage.getItem('onboarding_intent');

    if (intent === 'enroll') {
      // Button 1: Active admin → dashboard, otherwise → onboarding
      if (status.onboarding_status === 'Active') {
        navigate('/');
      } else {
        navigate('/onboarding');
      }
    } else if (intent === 'join') {
      // Button 2: Always → onboarding (wizard checks for pending invitations)
      navigate('/onboarding');
    } else {
      // Button 3 (app_login) or no intent: Active → dashboard, otherwise → onboarding
      if (status.onboarding_status === 'Active') {
        navigate('/');
      } else {
        navigate('/onboarding');
      }
    }
  }, [isAuthenticated, status, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="lg">
        <Paper
          elevation={4}
          sx={{ borderRadius: 3, overflow: 'hidden', width: '100%' }}
        >
          {/* Branded header band */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              px: 6, py: 4,
              textAlign: 'center',
              borderBottom: '4px solid',
              borderColor: 'secondary.main',
            }}
          >
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: '#FDD023', letterSpacing: 1 }}>
              FaceSheetApp
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
              Medical Facesheet Processing Platform &bull; by CityBilling, LLC
            </Typography>
          </Box>

          {/* Card area */}
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={3}>
              {/* Register / Admin */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Register / Enroll / Admin Login</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                    Create account with FaceSheetApp, create billing plan, establish HIPAA-compliant workspace for your practice or practices, create accounts for your employees/users.
                  </Typography>
                  <Button variant="contained" color="primary" fullWidth sx={{ whiteSpace: 'normal', lineHeight: 1.4, minHeight: '64px', fontSize: '0.875rem' }} onClick={() => {
                    sessionStorage.setItem('onboarding_intent', 'enroll');
                    login();
                  }}>
                    Register / Enroll / Admin Login
                  </Button>
                </Box>
              </Grid>

              {/* Employee */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Join Your Organization</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                    Join your organization's workspace with an invite from your organization as staff member or employee. You need to join your organization before being able to login to FaceSheetApp.
                  </Typography>
                  <Button variant="contained" color="primary" fullWidth sx={{ whiteSpace: 'normal', lineHeight: 1.4, minHeight: '64px', fontSize: '0.875rem' }} onClick={() => {
                    sessionStorage.setItem('onboarding_intent', 'join');
                    login();
                  }}>
                    Join Your Organization
                  </Button>
                </Box>
              </Grid>

              {/* Returning user */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>Login to FaceSheetApp</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                    Staff member or employee login to use FaceSheetApp.
                  </Typography>
                  <Button variant="contained" color="primary" fullWidth sx={{ whiteSpace: 'normal', lineHeight: 1.4, minHeight: '64px', fontSize: '0.875rem' }} onClick={() => {
                    sessionStorage.setItem('onboarding_intent', 'app_login');
                    login();
                  }}>
                    Login to FaceSheetApp
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Footer note */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="body2" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                ℹ️ Services available in the United States only
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                FaceSheetApp is exclusively for licensed US healthcare providers. Billing requires a US address.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Standard Microsoft MFA is required for all access. &nbsp;|&nbsp; Powered by Microsoft Entra External ID (v{import.meta.env.VITE_APP_VERSION})
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
